import DOMPurify from 'isomorphic-dompurify';

export const PREVIEW_WIDTH_PX = 794;

/**
 * The legacy preview was inserted into a Bootstrap page. Keep only the small
 * part of that baseline which is required by the generated templates and do
 * not override template-specific sizing or display rules.
 */
const PREVIEW_BASE_STYLE = `<style id="kw-preview-overrides">
*,*::before,*::after{box-sizing:border-box;}
html,body{
  margin:0!important;
  padding:0!important;
  min-height:0;
  overflow:visible!important;
  scrollbar-width:none;
  -ms-overflow-style:none;
}
html::-webkit-scrollbar,body::-webkit-scrollbar{display:none;width:0;height:0}
.template-section,.template-section.container,#template-section,.resume-container,.t40-container,
.cover-letter-container,.cover-letter-container .cover-letter,.cover-letter{
  overflow:visible!important;
  max-height:none!important;
  display:flow-root;
}
</style>`;

const PREVIEW_ALLOWED_TAGS = ['base', 'link', 'meta'];
const PREVIEW_ALLOWED_ATTR = [
  'class',
  'id',
  'style',
  'target',
  'rel',
  'href',
  'src',
  'srcset',
  'alt',
  'width',
  'height',
  'align',
  'valign',
  'bgcolor',
  'cellpadding',
  'cellspacing',
  'colspan',
  'rowspan',
  'role',
  'crossorigin',
  'as',
  'media',
  'type',
  'sizes',
  'name',
  'content',
  'charset',
  'lang',
  'loading',
  'contenteditable',
];

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function normalizeBaseUrl(baseUrl?: string): string | null {
  if (!baseUrl) {
    return null;
  }
  try {
    const parsed = new URL(baseUrl);
    return `${parsed.origin}/`;
  } catch {
    return null;
  }
}

function ensurePreviewHead(html: string, baseUrl?: string): string {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const baseTag =
    normalizedBaseUrl && !/<base\b/i.test(html)
      ? `<base href="${escapeAttribute(normalizedBaseUrl)}">`
      : '';
  const additions = `${baseTag}${PREVIEW_BASE_STYLE}`;

  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${additions}</head>`);
  }
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${additions}`);
  }
  return `${additions}${html}`;
}

/**
 * Prepare the full document returned by the resume renderer for srcDoc.
 * Keeping this separate from entry-description sanitization prevents a
 * fragment allowlist from accidentally becoming the preview contract.
 */
export function prepareResumePreviewDocument(html: string, baseUrl?: string): string {
  if (!html) {
    return '';
  }

  const clean = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: PREVIEW_ALLOWED_TAGS,
    ADD_ATTR: PREVIEW_ALLOWED_ATTR,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: [
      'onerror',
      'onload',
      'onclick',
      'onmouseover',
      'onfocus',
      'onblur',
      'onmouseenter',
      'onmouseleave',
    ],
    ALLOW_DATA_ATTR: true,
  });

  if (!clean) {
    return '';
  }

  const withFixedViewport = clean.replace(
    /<meta\s+[^>]*name\s*=\s*["']viewport["'][^>]*>/gi,
    `<meta name="viewport" content="width=${PREVIEW_WIDTH_PX}">`,
  );
  const ensuredViewport = /name\s*=\s*["']viewport["']/i.test(withFixedViewport)
    ? withFixedViewport
    : withFixedViewport.replace(
        /<head([^>]*)>/i,
        `<head$1><meta name="viewport" content="width=${PREVIEW_WIDTH_PX}">`,
      );
  const prepared = ensurePreviewHead(ensuredViewport, baseUrl);

  if (/^<!DOCTYPE/i.test(prepared)) {
    return prepared;
  }
  return `<!DOCTYPE html>${prepared}`;
}

/** Backwards-compatible name used by existing preview and cover-letter code. */
export function sanitizeResumePreviewHtml(html: string, baseUrl?: string): string {
  return prepareResumePreviewDocument(html, baseUrl);
}

/** Backwards-compatible helper used by the existing sanitization tests. */
export function injectPreviewOverflowOverrides(html: string): string {
  if (!html || html.includes('id="kw-preview-overrides"')) {
    return html;
  }
  return ensurePreviewHead(html);
}
